import React, { useCallback, useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';

import {
    Button,
    IconLock,
    ValidatedInput,
    LoadingEllipses,
} from '@trqk/components';
import { locale, getWebpackEnvVar } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { validateInputs } from '@trqk/hooks';

import { useToast } from '../../../hooks';
import { inputIds } from './constants';

const { REQUEST_AN_INVITATION } = queryStrings;

/**
 * @typedef {RequestInvitationModalInput}
 * @property {string} email email address that was checked
 * @property {string} statementId the statement id that was used on the tryout page
 */
interface RequestInvitationModalInput {
    email: string;
    statementId: string;
}

/**
 * Renders a form for the user to request an invitation to a free trial. If they already have an
 * invitation, they will be redirected to the CreateAccount page.
 * @param {ModalContentProps<RequestInvitationModalInput>} props
 */
export const RequestInvitation = ({
    remove,
    input,
}: ModalContentProps<RequestInvitationModalInput>): JSX.Element => {
    const invitationsUrl = getWebpackEnvVar('web', 'INVITATIONS_URL');
    const initialEmail = input?.email ?? '';
    const disableEmailInput = initialEmail.length !== 0;
    const [email, setEmail] = useState(initialEmail);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [company, setCompany] = useState('');
    const [didSubmit, setDidSubmit] = useState(false);
    const [validationOutputs, setValidationOutputs] = useState<
        ValidationOutput[]
    >([]);

    const validate = useCallback(
        () =>
            validateInputs([
                {
                    inputId: inputIds.requestInvitationFirstName,
                    label: 'First name',
                    type: 'name',
                    value: firstName,
                    required: true,
                },
                {
                    inputId: inputIds.requestInvitationLastName,
                    label: 'Last name',
                    type: 'name',
                    value: lastName,
                    required: true,
                },
                {
                    inputId: inputIds.requestInvitationCompany,
                    label: 'Company',
                    type: 'company',
                    value: company,
                    required: true,
                },
                {
                    inputId: inputIds.requestInvitationEmail,
                    label: 'Email',
                    type: 'email',
                    value: email,
                    required: true,
                },
            ]),
        [firstName, lastName, company, email],
    );

    useEffect(() => {
        if (didSubmit) {
            const validationResults = validate();

            setValidationOutputs(validationResults.validationOutputs);
        }
    }, [didSubmit, validate]);

    const toast = useToast();
    const [requestAnInvitation, { loading }] = useMutation(
        REQUEST_AN_INVITATION,
        {
            onCompleted: () => {
                remove({
                    canceled: false,
                    success: false,
                });

                toast.add({
                    kind: 'info-banner',
                    content:
                        locale.enUS.STATEMENTS_PREVIEW.MODALS.REQUEST_INVITATION
                            .CONFIRMATIONS.REQUEST_AN_INVITATION,
                });
            },
            onError: (): void => {
                toast.add({
                    kind: 'warning-banner',
                    content:
                        locale.enUS.STATEMENTS_PREVIEW.MODALS.REQUEST_INVITATION
                            .ERRORS.REQUEST_AN_INVITATION,
                });
            },
        },
    );

    const handleSubmit = (): void => {
        setDidSubmit(true);

        const validationResults = validate();

        if (!validationResults.hasValidationErrors) {
            requestAnInvitation({
                variables: {
                    company,
                    email,
                    firstName,
                    lastName,
                    invitationsUrl,
                },
            });
        } else {
            setValidationOutputs(validationResults.validationOutputs);
        }
    };

    return (
        <div
            className="px-12 py-10 bg-white rounded-lg border-l-4 border-secondary-500"
            style={{ width: 450 }}
        >
            <div className="mb-10">
                <div className="flex items-center">
                    <IconLock fillColor="secondary-500" width={35} />
                    <h5 className="ml-2">
                        {
                            locale.enUS.STATEMENTS_PREVIEW.MODALS
                                .REQUEST_INVITATION.REQUEST_AN_INVITATION
                        }
                    </h5>
                </div>
            </div>
            <ValidatedInput
                addClasses="my-3 border border-tertiary-200"
                inputId={inputIds.requestInvitationFirstName}
                icon="name"
                type="text"
                placeholder="First name"
                value={firstName}
                handleChange={setFirstName}
                validationOutputs={validationOutputs}
            />
            <ValidatedInput
                addClasses="my-3 border border-tertiary-200"
                inputId={inputIds.requestInvitationLastName}
                icon="name"
                type="text"
                placeholder="Last name"
                value={lastName}
                handleChange={setLastName}
                validationOutputs={validationOutputs}
            />
            <ValidatedInput
                addClasses="my-3 border border-tertiary-200"
                inputId={inputIds.requestInvitationCompany}
                icon="company"
                type="text"
                placeholder="Company"
                value={company}
                handleChange={setCompany}
                validationOutputs={validationOutputs}
            />
            <ValidatedInput
                disabled={disableEmailInput}
                addClasses="my-3 border border-tertiary-200"
                inputId={inputIds.requestInvitationEmail}
                icon="email"
                type="text"
                placeholder="Email"
                value={email}
                handleChange={setEmail}
                validationOutputs={validationOutputs}
            />
            <div className="flex justify-end mt-6">
                <Button
                    color="tertiary-light-bg"
                    autoWidth={true}
                    addClasses="mr-4"
                    handleClick={(): void => {
                        remove({
                            canceled: true,
                            success: false,
                        });
                    }}
                >
                    <div className="text-center mx-3">
                        {
                            locale.enUS.STATEMENTS_PREVIEW.MODALS
                                .REQUEST_INVITATION.BUTTONS.CANCEL
                        }
                    </div>
                </Button>
                <Button color="primary-light-bg" handleClick={handleSubmit}>
                    <div className="text-center mx-3">
                        {!loading && (
                            <div>
                                {
                                    locale.enUS.STATEMENTS_PREVIEW.MODALS
                                        .REQUEST_INVITATION.BUTTONS.REQUEST
                                }
                            </div>
                        )}
                        {loading && <LoadingEllipses width={40} />}
                    </div>
                </Button>
            </div>
        </div>
    );
};
